import { Quaternion } from './quaternion';

export interface Vector3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Get vector length
 * @param vector Vector
 */
export function length(vector: Vector3): number {
  return Math.sqrt(Math.pow(vector.x, 2) + Math.pow(vector.y, 2) + Math.pow(vector.z, 2));
}

export function toEulerAngles(quaternion: Quaternion): Vector3 {
  // reference http://stackoverflow.com/questions/12088610/conversion-between-euler-quaternion-like-in-unity3d-engine
  const sqw = quaternion.w * quaternion.w;
  const sqx = quaternion.x * quaternion.x;
  const sqy = quaternion.y * quaternion.y;
  const sqz = quaternion.z * quaternion.z;
  const unit = sqx + sqy + sqz + sqw; // if normalised is one, otherwise is correction factor
  const test = quaternion.x * quaternion.w - quaternion.y * quaternion.z;
  let x;
  let y;
  let z;

  if (test > 0.4995 * unit) {
    // singularity at north pole
    y = 2 * Math.atan2(quaternion.y, quaternion.x);
    x = Math.PI / 2;
    z = 0;
  } else if (test < -0.4995 * unit) {
    // singularity at south pole
    y = -2 * Math.atan2(quaternion.y, quaternion.x);
    x = -Math.PI / 2;
    z = 0;
  } else {
    const q = { x: quaternion.w, y: quaternion.z, z: quaternion.x, w: quaternion.y };
    y = Math.atan2(2 * q.x * q.w + 2 * q.y * q.z, 1 - 2 * (q.z * q.z + q.w * q.w)); // Yaw
    x = Math.asin(2 * (q.x * q.z - q.w * q.y)); // Pitch
    z = Math.atan2(2 * q.x * q.y + 2 * q.z * q.w, 1 - 2 * (q.y * q.y + q.z * q.z)); // Roll
  }
  const anglesDegrees = multiplyScalar({ x, y, z }, rad2Deg);
  return normalizeAngles(anglesDegrees);
}

export function multiplyScalar(vector: Vector3, scalar: number): Vector3 {
  return {
    x: vector.x * scalar,
    y: vector.y * scalar,
    z: vector.z * scalar,
  };
}

function normalizeAngles(angles: Vector3): Vector3 {
  return {
    x: normalizeAngle(angles.x),
    y: normalizeAngle(angles.y),
    z: normalizeAngle(angles.z),
  };
}

function normalizeAngle(angle: number): number {
  while (angle > 360) {
    angle -= 360;
  }
  while (angle < 0) {
    angle += 360;
  }
  return angle;
}

const rad2Deg = 57.29578;
